// Email composer modal. Supports:
//  - Template picker (user-scoped + shared)
//  - Merge field insertion for contact / account / deal / user
//  - "Send via M365" when Azure is configured (falls back to a helpful message)
//  - "Log externally-sent" as an alternative for teams without Azure wired
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth-store";
import { applyMerge, enrichContactForMerge, AVAILABLE_TOKENS, type MergeContext } from "@/lib/merge-fields";
import { X, Send, FileText, Info } from "lucide-react";

export interface EmailComposerProps {
  open: boolean;
  onClose: () => void;

  /** Pre-fills the "To" field + links the logged activity. */
  contact?: any;
  account?: any;
  deal?: any;

  /** Query keys to invalidate after send (e.g. to refresh the activity timeline). */
  invalidateQueryKeys?: unknown[][];
}

export function EmailComposer(props: EmailComposerProps) {
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showMerge, setShowMerge] = useState(false);
  const [templateId, setTemplateId] = useState<string>("");

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Reset fields when opened with a new contact.
  useEffect(() => {
    if (!props.open) return;
    setTo(props.contact?.email ?? "");
    setCc(""); setBcc(""); setSubject(""); setBody(""); setTemplateId("");
  }, [props.open, props.contact?.id]);

  const status = useQuery({
    queryKey: ["/graph/status"],
    queryFn: async () => (await api.get("/graph/status")).data as { azureConfigured: boolean; canSend: boolean; canLogManually: boolean },
    enabled: props.open,
  });

  const templates = useQuery({
    queryKey: ["/email-templates"],
    queryFn: async () => (await api.get("/email-templates")).data.data as any[],
    enabled: props.open,
  });

  const mergeCtx: MergeContext = useMemo(() => ({
    contact: enrichContactForMerge(props.contact),
    account: props.account ?? props.contact?.account,
    deal: props.deal,
    user: me ? { name: me.name, email: me.email } : undefined,
  }), [props.contact, props.account, props.deal, me]);

  function applyTemplate(id: string) {
    setTemplateId(id);
    if (!id) return;
    const t = templates.data?.find((x) => x.id === id);
    if (!t) return;
    setSubject(applyMerge(t.subject, mergeCtx));
    setBody(applyMerge(t.bodyHtml, mergeCtx));
  }

  function insertToken(token: string) {
    const ta = bodyRef.current;
    if (!ta) { setBody((b) => b + token); return; }
    const start = ta.selectionStart ?? body.length;
    const end   = ta.selectionEnd   ?? body.length;
    const next  = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    queueMicrotask(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  function invalidate() {
    (props.invalidateQueryKeys ?? []).forEach((key) => qc.invalidateQueries({ queryKey: key }));
  }

  const sendMut = useMutation({
    mutationFn: async () => {
      const payload = {
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc ? cc.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
        bcc: bcc ? bcc.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
        subject,
        bodyHtml: body.replace(/\n/g, "<br/>"),
        contactId: props.contact?.id,
        accountId: props.account?.id ?? props.contact?.account?.id,
        dealId: props.deal?.id,
      };
      return (await api.post("/graph/sendmail", payload)).data;
    },
    onSuccess: () => { invalidate(); props.onClose(); },
  });

  const logMut = useMutation({
    mutationFn: async () => {
      const payload = {
        subject,
        body,
        direction: "outbound" as const,
        contactId: props.contact?.id,
        accountId: props.account?.id ?? props.contact?.account?.id,
        dealId: props.deal?.id,
      };
      return (await api.post("/graph/log-email", payload)).data;
    },
    onSuccess: () => { invalidate(); props.onClose(); },
  });

  if (!props.open) return null;

  const canSend = status.data?.canSend;
  const sendError = (sendMut.error as any)?.response?.data?.message ?? (sendMut.error as any)?.message;
  const logError  = (logMut.error  as any)?.response?.data?.message ?? (logMut.error  as any)?.message;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-2xl flex-col rounded-lg border bg-card shadow-xl">
        <header className="flex items-center justify-between border-b px-4 py-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            New Email
            {!canSend && status.isFetched && (
              <Badge variant="secondary" className="gap-1">
                <Info className="h-3 w-3" />
                Azure AD not configured — log-only mode
              </Badge>
            )}
          </div>
          <button onClick={props.onClose} className="rounded-md p-1 hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 overflow-y-auto p-4">
          <div className="grid gap-2 md:grid-cols-[80px_1fr] md:items-center">
            <Label htmlFor="em-to">To</Label>
            <Input id="em-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com, another@example.com" />
            <Label htmlFor="em-cc">CC</Label>
            <Input id="em-cc" value={cc} onChange={(e) => setCc(e.target.value)} />
            <Label htmlFor="em-bcc">BCC</Label>
            <Input id="em-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} />
            <Label htmlFor="em-template">Template</Label>
            <Select id="em-template" value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">— No template —</option>
              {templates.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.isShared ? " (shared)" : ""}
                </option>
              ))}
            </Select>
            <Label htmlFor="em-subject">Subject</Label>
            <Input id="em-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label htmlFor="em-body">Body</Label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowMerge((v) => !v)}
                  className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                >
                  <FileText className="h-3 w-3" /> Insert merge field
                </button>
                {showMerge && (
                  <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border bg-popover shadow-lg">
                    <ul className="max-h-64 overflow-y-auto text-sm">
                      {AVAILABLE_TOKENS.map((t) => (
                        <li key={t.token}>
                          <button
                            type="button"
                            onClick={() => { insertToken(t.token); setShowMerge(false); }}
                            className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-muted"
                          >
                            <span>{t.label}</span>
                            <code className="text-xs text-muted-foreground">{t.token}</code>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <textarea
              id="em-body"
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[240px] w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Hi {{contact.firstName}}, ..."
            />
            {/* Preview — apply merge context so user sees what the recipient will get */}
            {(subject || body) && (
              <details className="mt-2 rounded-md border bg-muted/40 p-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground">Preview (with merge fields resolved)</summary>
                <div className="mt-2 space-y-1">
                  <div><span className="font-medium">Subject:</span> {applyMerge(subject, mergeCtx)}</div>
                  <div className="whitespace-pre-wrap"><span className="font-medium">Body:</span>{"\n"}{applyMerge(body, mergeCtx)}</div>
                </div>
              </details>
            )}
          </div>

          {(sendError || logError) && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{sendError || logError}</p>
          )}
        </div>

        <footer className="flex items-center justify-between border-t bg-card px-4 py-3">
          <span className="text-xs text-muted-foreground">
            {canSend ? "Sends via your Microsoft 365 account." : "Log-only: send the email from Outlook, then record the touchpoint here."}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={props.onClose}>Cancel</Button>
            <Button
              variant="outline"
              onClick={() => logMut.mutate()}
              disabled={!subject || logMut.isPending}
            >
              {logMut.isPending ? "Logging…" : "Log as sent"}
            </Button>
            {canSend && (
              <Button onClick={() => sendMut.mutate()} disabled={!to || !subject || sendMut.isPending}>
                <Send className="mr-1 h-4 w-4" />
                {sendMut.isPending ? "Sending…" : "Send"}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
